import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import { Plus, Edit, ChevronDown, ChevronUp } from 'lucide-react';

const ALL_DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const DAY_LABELS: Record<string, string> = {
  MON: 'Mon', TUE: 'Tue', WED: 'Wed', THU: 'Thu', FRI: 'Fri', SAT: 'Sat', SUN: 'Sun',
};

export default function AdminPlansPage() {
  const navigate = useNavigate();
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>({});
  const [expandedLevel, setExpandedLevel] = useState<Record<string, boolean>>({});

  const { data: plans } = useQuery({
    queryKey: ['adminPlans'],
    queryFn: () => api.get('/investments/plans').then(r => r.data),
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Investment Plans</h1>
          <p className="text-sm text-slate-400">{plans?.length || 0} plans configured</p>
        </div>
        <Button size="sm" onClick={() => navigate('/admin/plans/new')}>
          <Plus className="w-4 h-4 mr-1" />Add Plan
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {plans?.map((plan: any) => {
          const poolAmt = parseFloat(plan.levelIncomePoolAmount || '0');
          const poolDur = plan.levelIncomeDuration || 200;
          const dailyPool = poolDur > 0 ? poolAmt / poolDur : 0;
          const isExpanded = expandedCards[plan.id] || false;
          const isLevelExpanded = expandedLevel[plan.id] || false;

          return (
            <div key={plan.id} className="bg-fintech-800/80 border border-white/[0.06] rounded-2xl p-5 hover:border-white/[0.1] transition-colors">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-bold text-white">{plan.name}</h3>
                  <p className="text-sm text-slate-400">{plan.description}</p>
                </div>
                <Badge status={plan.isActive ? 'ACTIVE' : 'INACTIVE'} />
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                <div className="p-2 bg-white/[0.02] rounded"><p className="text-slate-400 text-xs">Price</p><p className="font-medium text-white">{formatCurrency(plan.minAmount)}</p></div>
                <div className="p-2 bg-white/[0.02] rounded"><p className="text-slate-400 text-xs">Daily Rate</p><p className="font-medium text-emerald-400">{plan.dailyRate}%</p></div>
                <div className="p-2 bg-white/[0.02] rounded"><p className="text-slate-400 text-xs">Duration</p><p className="font-medium text-white">{plan.duration} days</p></div>
                <div className="p-2 bg-white/[0.02] rounded"><p className="text-slate-400 text-xs">Max Return</p><p className="font-medium text-white">{plan.maxEarningMultiple}x</p></div>
              </div>

              <div className="mb-3">
                <p className="text-xs font-semibold text-slate-400 uppercase mb-2">Working Days</p>
                <div className="flex gap-1.5">
                  {ALL_DAYS.map(day => {
                    const active = plan.workingDays?.split(',').includes(day);
                    return (
                      <span key={day} className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                        active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/[0.04] text-slate-500'
                      }`}>{DAY_LABELS[day]}</span>
                    );
                  })}
                </div>
              </div>

              <div className="border-t border-white/[0.06] pt-3 mb-3">
                <button className="w-full flex items-center justify-between text-xs font-semibold text-slate-400 uppercase mb-2 hover:text-slate-300 transition-colors"
                  onClick={() => setExpandedLevel(p => ({ ...p, [plan.id]: !p[plan.id] }))}>
                  <span>Level Income Pool</span>
                  {isLevelExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="p-2 bg-primary-500/10 rounded"><p className="text-slate-400 text-xs">Pool Amount</p><p className="font-medium text-primary-400">{formatCurrency(plan.levelIncomePoolAmount)}</p></div>
                  <div className="p-2 bg-primary-500/10 rounded"><p className="text-slate-400 text-xs">Pool Duration</p><p className="font-medium text-white">{plan.levelIncomeDuration} days</p></div>
                </div>
                {isLevelExpanded && (
                  <div className="mt-2 p-3 bg-white/[0.02] rounded-lg text-xs text-slate-400 space-y-1">
                    <p>Daily pool = {formatCurrency(plan.levelIncomePoolAmount)} / {plan.levelIncomeDuration} days = <span className="text-primary-400 font-medium">{formatCurrency(dailyPool)}/day</span></p>
                    {plan.levelDistributionConfig && (
                      <pre className="bg-white/[0.04] p-2 rounded text-[10px] text-slate-400 overflow-x-auto whitespace-pre-wrap mt-1">{plan.levelDistributionConfig}</pre>
                    )}
                  </div>
                )}
              </div>

              {isExpanded && (
                <div className="border-t border-white/[0.06] pt-3 mb-3">
                  <p className="text-xs font-semibold text-slate-400 uppercase mb-2">Extra Options</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="p-2 bg-white/[0.02] rounded"><p className="text-slate-400 text-xs">Auto Renewal</p><p className="font-medium text-white">{plan.autoRenewal ? 'Yes' : 'No'}</p></div>
                    <div className="p-2 bg-white/[0.02] rounded"><p className="text-slate-400 text-xs">Withdrawal Restriction</p><p className="font-medium text-white">{plan.withdrawalRestriction} days</p></div>
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <Button variant="secondary" size="sm" className="flex-1" onClick={() => setExpandedCards(p => ({ ...p, [plan.id]: !p[plan.id] }))}>
                  {isExpanded ? <ChevronUp className="w-3.5 h-3.5 mr-1" /> : <ChevronDown className="w-3.5 h-3.5 mr-1" />}
                  {isExpanded ? 'Less' : 'More'}
                </Button>
                <Button variant="primary" size="sm" className="flex-1" onClick={() => navigate(`/admin/plans/${plan.id}/edit`)}>
                  <Edit className="w-3.5 h-3.5 mr-1" />Edit
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
